import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { zeroPad } from 'react-countdown';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import { AutoFocusRef, useAutoFocus } from '@/data/hook/useAutoFocus';
import useToggle from '@/data/hook/useToggle';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempChapter } from '@/models/Chapter';
import { TempSubChapter } from '@/models/Subchapter';
import { bodyText } from '@/styles/styles';
import { getDisabledDirectLessons } from '@/utils/courseStudio/directLessons';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import DirectLessonBookSubChapter from './DirectLessonBookSubChapter';

type Props = {
  lessonIndex: number;
  chapterIndex: number;
  chapter: TempChapter;
  studioPermissions: StudioPermissions;
  removeChapter: () => void;
  setChapterElementRef: (index: number) => (el: AutoFocusRef) => void;
  startAllOpen?: boolean;
};

export default function DirectLessonBookChapter(props: Props) {
  const {
    lessonIndex,
    chapterIndex,
    chapter,
    studioPermissions,
    removeChapter,
    setChapterElementRef,
    startAllOpen,
  } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    control,
    formState: { errors },
  } = useFormContext<CourseStudioFormFields>();

  const {
    fields: subchapters,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `directLessons.${lessonIndex}.chapters.${chapterIndex}.items`,
    keyName: 'tempId',
  });

  const { isOpen, toggle, open } = useToggle(startAllOpen);

  const disabled = getDisabledDirectLessons(studioPermissions);

  function handleAddSubChapter() {
    const newSubChapter = new TempSubChapter();

    append(newSubChapter);

    open();
  }

  const { setElementRef } = useAutoFocus(subchapters.length);

  return (
    <li className="flex flex-col gap-2.5">
      <ModularCard className={{ base: 'pb-3.5 border-none shadow-none' }}>
        <ModularCard.Header
          title={chapter.title}
          onOpen={toggle}
          isOpen={isOpen}
          className={{
            base: 'p-0 pb-3.5',
            title: 'font-400',
            section: 'max-w-full w-[90%]',
          }}
          titleFallback={
            <Controller
              rules={{
                required: {
                  value: true,
                  message: tCommon('form.required', {
                    field: tCommon('chapter').toLowerCase(),
                  }),
                },
              }}
              control={control}
              name={`directLessons.${lessonIndex}.chapters.${chapterIndex}.title`}
              render={({ field: { onChange, value = '' } }) => (
                <ConditionalWrapper
                  condition={disabled}
                  wrapper={
                    <Tooltip
                      text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                      classNameContainer="flex w-full"
                    />
                  }
                >
                  <SecondaryInput
                    ref={setChapterElementRef(chapterIndex)}
                    label={String(zeroPad(chapterIndex + 1, 2)) + ' - '}
                    value={value}
                    placeholder={t('chapterPlaceholder')}
                    onChange={onChange}
                    onFocus={open}
                    disabled={disabled}
                    error={
                      errors.directLessons?.[lessonIndex]?.chapters?.[
                        chapterIndex
                      ]?.title?.message
                    }
                    onClick={e => e.stopPropagation()}
                    className={{
                      label: 'font-500',
                      input: 'border-base-content/25 font-400',
                    }}
                    clearWrapper={
                      <ConditionalWrapper
                        condition={
                          !studioPermissions.allowOtherSteps.advancedEditing
                        }
                        wrapper={
                          <Tooltip
                            text={t('notAllowedLimitedEditing')}
                            classNameContainer="flex"
                          />
                        }
                      >
                        <IconButton
                          disabled={
                            !studioPermissions.allowOtherSteps.advancedEditing
                          }
                          type="button"
                          icon={
                            <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                          }
                          onClick={removeChapter}
                        />
                      </ConditionalWrapper>
                    }
                  />
                </ConditionalWrapper>
              )}
            />
          }
        />
        <ModularCard.Accordion isOpen={isOpen} className={{ base: 'px-4' }}>
          <ul className="flex flex-col gap-2.5 pb-3.5">
            <ConditionalRenderer
              condition={subchapters.length}
              fallback={<p className={bodyText}>{t('noSubChapters')}</p>}
            >
              {subchapters.map((subChapter, subChapterIndex) => (
                <DirectLessonBookSubChapter
                  key={subChapter.id || subChapter.tempId}
                  lessonIndex={lessonIndex}
                  chapterIndex={chapterIndex}
                  subChapterIndex={subChapterIndex}
                  removeSubChapter={() => remove(subChapterIndex)}
                  setSubChapterElementRef={setElementRef}
                  studioPermissions={studioPermissions}
                />
              ))}
            </ConditionalRenderer>
          </ul>
        </ModularCard.Accordion>

        <div className="mt-auto shrink-0 px-4">
          <ConditionalWrapper
            condition={!studioPermissions.allowOtherSteps.advancedEditing}
            wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
          >
            <MainButton
              disabled={disabled}
              color="custom"
              className="text-primary font-500 capitalize"
              icon={<PlusIcon />}
              text={t('addSubChapter')}
              onClick={handleAddSubChapter}
            />
          </ConditionalWrapper>
        </div>
      </ModularCard>
    </li>
  );
}
